/**
 * FeedbackWidget.tsx
 * Floating "Send Feedback" button + modal form (mailto pattern).
 *
 * Integrated from the `feedback-widget` module. Adapted for Absence Budget:
 * - no react-i18next — plain string literals
 * - no FocusManager — simple focus on open
 * - app CSS classes (btn, input-group__*, modal__close) + a dedicated
 *   `.feedback-widget__*` CSS block in styles/components.css
 */

import React, { useState, useEffect, useRef } from 'react';
import { userFeedbackManager, buildMailtoUrl } from '@/utils/userFeedback';
import type { UserFeedback } from '@/utils/userFeedback';

type FeedbackFormData = {
  type: UserFeedback['type'];
  title: string;
  description: string;
  category: string;
  priority: UserFeedback['priority'];
};

interface FeedbackWidgetFormProps {
  isOpen: boolean;
  onClose: () => void;
  // Accept `undefined` (exactOptionalPropertyTypes) so callers can pass
  // possibly-undefined values directly.
  initialType?: UserFeedback['type'] | undefined;
  initialCategory?: string | undefined;
  initialError?: string | undefined;
}

export function FeedbackWidgetForm({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}: FeedbackWidgetFormProps): React.ReactElement | null {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FeedbackFormData>({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError ?? '',
    category: initialCategory,
    priority: 'medium',
  });

  const feedbackTypes: { value: UserFeedback['type']; label: string }[] = [
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'general_feedback', label: 'General Feedback' },
    { value: 'usability_issue', label: 'Usability Issue' },
    { value: 'performance_issue', label: 'Performance Issue' },
  ];

  const categories = [
    'general',
    'attendance',
    'policy',
    'grading',
    'ui_ux',
    'performance',
    'bug',
    'feature_request',
    'accessibility',
  ];

  const priorities: { value: UserFeedback['priority']; label: string }[] = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  const contentRef = useRef<HTMLDivElement>(null);

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError ?? '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  // Simple focus on open (no external focus-trap utility in this app)
  useEffect(() => {
    if (isOpen) contentRef.current?.focus();
  }, [isOpen]);

  // Hooks are all called above; early return is safe.
  if (!isOpen) return null;

  const handleChange = (field: keyof FeedbackFormData, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }) as FeedbackFormData);

  const titleCase = (s: string) =>
    s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, url: window.location.href };
      const id = userFeedbackManager.submitFeedback(payload);
      setFeedbackId(id);

      const mailtoUrl = buildMailtoUrl(payload);
      if (mailtoUrl) window.location.href = mailtoUrl;

      setSubmitted(true);
    } catch (err) {
      console.error('Feedback submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="feedback-widget__overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Send Feedback"
    >
      <div ref={contentRef} className="feedback-widget__panel" tabIndex={-1}>
        <div className="feedback-widget__body">
          {/* Header */}
          <div className="feedback-widget__header">
            <h2 className="feedback-widget__title">Send Feedback</h2>
            <button
              className="modal__close"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {submitted ? (
            /* Success state */
            <div className="feedback-widget__success">
              <p className="feedback-widget__success-title">Thank you!</p>
              <p className="feedback-widget__success-text">
                Your mail client should have opened. Send the pre-filled email to complete.
              </p>
              {feedbackId && <p className="feedback-widget__ref">Ref: {feedbackId}</p>}
              <button
                type="button"
                className="btn btn--primary btn--md"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Type */}
              <div className="feedback-widget__field">
                <label htmlFor="fb-type" className="feedback-widget__label">
                  Type
                </label>
                <select
                  id="fb-type"
                  className="input-group__select"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                >
                  {feedbackTypes.map((ft) => (
                    <option key={ft.value} value={ft.value}>
                      {ft.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div className="feedback-widget__field">
                <label htmlFor="fb-category" className="feedback-widget__label">
                  Category
                </label>
                <select
                  id="fb-category"
                  className="input-group__select"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {titleCase(c)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div className="feedback-widget__field">
                <span className="feedback-widget__label">Priority</span>
                <div className="feedback-widget__priority-row">
                  {priorities.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      className={`btn btn--sm ${
                        formData.priority === p.value ? 'btn--primary' : 'btn--secondary'
                      }`}
                      onClick={() => handleChange('priority', p.value)}
                      aria-pressed={formData.priority === p.value}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="feedback-widget__field">
                <label htmlFor="fb-title" className="feedback-widget__label">
                  Title *
                </label>
                <input
                  id="fb-title"
                  className="input-group__input"
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Brief summary"
                />
              </div>

              {/* Description */}
              <div className="feedback-widget__field">
                <label htmlFor="fb-description" className="feedback-widget__label">
                  Description *
                </label>
                <textarea
                  id="fb-description"
                  className="input-group__textarea"
                  rows={5}
                  required
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Please describe in detail..."
                />
              </div>

              {/* Actions */}
              <div className="feedback-widget__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--md"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary btn--md"
                  disabled={
                    isSubmitting || !formData.title.trim() || !formData.description.trim()
                  }
                >
                  {isSubmitting ? 'Opening mail…' : 'Send Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Floating trigger button ----------

interface FeedbackButtonProps {
  onOpen: (
    type?: UserFeedback['type'],
    category?: string,
    error?: string,
  ) => void;
}

export function FeedbackButton({ onOpen }: FeedbackButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      className="feedback-widget__fab"
      onClick={() => onOpen()}
      aria-label="Send feedback"
      title="Send feedback"
    >
      <svg
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    </button>
  );
}
